import Vue from 'vue'
import Router from 'vue-router'
import MainLayout from '@/MainLayout'
import Dashboard from '@/features/dashboard'
import Calendar from '@/features/calendar'

import ContentBriefs from '@/features/content-briefs'
import contentBriefRoutes from '@/features/content-briefs/content-briefs.routes'
import reportsRoutes from '@/features/reports/reports.routes'

import UserAccount from '@/features/account'
import Reports from '@/features/reports'
import CampaignGuidelines from '@/features/campaign-guidelines'
import UrlPlanning from '@/features/url-planning/UrlPlanning'
import LinkBuilding from '@/features/link-building/LinkBuilding'
import BaseRouterView from '@/common/BaseRouterView'

import UserManagement from '@/features/user-management'

import Status from '@/features/status/Status'
import Category from '@/features/category/Category'
import Sections from '@/features/sections/Sections'
import Reporting from '@/features/reporting/Reporting'
import ClientNote from '@/features/client-note/ClientNote'
import Importer from '@/features/importer/Importer'
import Links from '@/features/links/Links'
import RankReports from '@/features/rank-reports/RankReports'
import SerpTracker from '@/features/serp-tracker/SerpTracker'
import SerpTrackerViewHistory from '@/features/serp-tracker/ViewHistory'
import KeywordCensus from '@/features/keyword-census/KeywordCensus'
import UrlCensus from '@/features/url-census/UrlCensus'
import Auth from '@/features/auth'
import Login from '@/features/auth/Login'
import ForgotPassword from '@/features/auth/ForgotPassword'
import Register from '@/features/auth/Register'

import ColorTesting from '@/features/color-testing'
import GeneralInfo from '@/features/setup-wizard/GeneralInfo'
import LocalityInfo from '@/features/setup-wizard/LocalityInfo'
import CompetitorInfo from '@/features/setup-wizard/CompetitorInfo'
import AccountChecklist from '@/features/setup-wizard/AccountChecklist'
import Authorization from '@/features/authorization'
import Groups from '@/features/authorization/Groups'
import Permissions from '@/features/authorization/Permissions'
import NotFound from '@/NotFound'
import AccessDenied from '@/AccessDenied'
import store from './store/'

import { workScreenRoutes, clientSettingsRoutes, systemRoutes } from './common/BaseAgencyRoutes'

// Redirect legacy hash-based routes under /dashboard#/ to the correct path with history-based routing
(function redirectLegacyDashboardHash() {
  const { pathname, hash, search } = window.location

  // Normalize /dashboard and /dashboard/
  const isDashboard =
    pathname === '/dashboard' || pathname === '/dashboard/'

  if (isDashboard && hash.startsWith('#/')) {
    const newPath = hash.slice(1) // remove '#'
    window.history.replaceState(null, '', newPath + search)
  }
})()

const children = [
  {
    path: 'edit',
    name: 'edit'
  },
  {
    path: 'upload',
    name: 'upload'
  },
  {
    path: 'review',
    name: 'review'
  },
  {
    path: 'children',
    name: 'children'
  }
]
const addChildMethod = obj => {
  if (obj.children) {
    obj.children = [...obj.children, ...children]
  } else {
    obj.children = [...children]
  }
}

workScreenRoutes.forEach(addChildMethod)
clientSettingsRoutes.forEach(addChildMethod)
systemRoutes.forEach(addChildMethod)

Vue.use(Router)
/**
 * Main Route for application @see https://router.vuejs.org/ru/guide/#javascript
 * @type {VueRouter}
 */
const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '',
      name: 'main',
      component: MainLayout,
      redirect: '/reports',
      children: [
        {
          path: '/color-testing',
          name: 'color-testing',
          component: ColorTesting
        },
        {
          path: '/setup-wizard',
          name: 'setup-wizard',
          component: BaseRouterView,
          redirect: { name: 'setup-wizard-general-info' },
          children: [
            {
              path: 'general-info',
              name: 'setup-wizard-general-info',
              component: GeneralInfo
            },
            {
              path: 'locality-info',
              name: 'setup-wizard-locality-info',
              component: LocalityInfo,
              props: true
            },
            {
              path: 'competitor-info',
              name: 'setup-wizard-competitor-info',
              component: CompetitorInfo,
              props: true
            }

          ]
        },
        // dashboard routes
        {
          path: '/dashboard',
          name: 'dashboard',
          redirect: '/reports',
          component: Dashboard
        },
        {
          path: '/content-briefs',
          name: 'content-briefs',
          component: ContentBriefs,
          children: [
            ...contentBriefRoutes
          ]
        },
        {
          path: '/reports',
          name: 'reports',
          redirect: { name: 'rankzone' },
          component: Reports,
          children: [
            ...reportsRoutes,
            {
              path: 'link-building',
              name: 'link-building',
              component: LinkBuilding,
              // extract query data and convert it to props
              props: (route) => ({ 'content-brief': route.query.contentBrief })
            }
          ]
        },
        // used to skip html loading
        {
          path: '/skip_update',
          name: 'skip_update'
        },
        {
          path: '/strategy',
          name: 'strategy',
          component: BaseRouterView,
          redirect: { name: 'keyword-census' },
          children: [
            {
              path: 'url-planner',
              name: 'url-planner',
              component: UrlPlanning,
              // extract query data and convert it to props
              props: (route) => ({ filters: route.query.filters, integrationStatus: route.query.integration_status })
            },
            {
              path: 'calendar',
              name: 'calendar',
              component: Calendar,
              // extract query data and convert it to props
              props: (route) => ({ modal: route.query.modal, 'url-filters': route.query.filters })
            },
            {
              path: 'campaign-guidelines',
              name: 'campaign-guidelines',
              component: CampaignGuidelines
            },
            {
              path: 'project-notes',
              name: 'project-notes',
              component: ClientNote
            },
            {
              path: 'keyword-census/:id([0-9a-f]{32})?',
              name: 'keyword-census',
              component: KeywordCensus,
              props: (route) => ({ 'filterUrl': route.query.url, 'urlClientId': route.query.clientId })
            },
            {
              path: 'url-census/:id([0-9a-f]{32})?',
              name: 'url-census',
              component: UrlCensus
            },
            ...workScreenRoutes
          ]
        },
        {
          path: '/settings',
          name: 'settings',
          component: BaseRouterView,
          redirect: { name: 'section' },
          children: [
            {
              path: 'account-checklist',
              name: 'account-checklist',
              component: AccountChecklist
            },
            {
              path: 'project-membership',
              name: 'project-membership',
              component: UserManagement
            },
            {
              path: 'user-account',
              name: 'user-account',
              component: UserAccount
            },
            {
              path: 'section',
              name: 'section',
              component: Sections
            },
            {
              path: 'attribute_manager',
              name: 'attribute_manager',
              redirect: { name: 'keyword-attributes' }
            },
            {
              path: 'url_attribute',
              name: 'url_attribute',
              redirect: { name: 'url-attributes' }
            },
            ...clientSettingsRoutes
          ]
        },
        {
          path: '/data',
          name: 'data',
          component: BaseRouterView,
          redirect: { name: 'rank_report' },
          children: [
            {
              path: 'imports',
              name: 'imports',
              component: Importer
            },
            {
              path: 'exports',
              name: 'exports',
              component: Reporting
            },
            {
              path: 'link',
              name: 'link',
              component: Links
            }
          ]
        },
        {
          path: '/system',
          name: 'system',
          component: BaseRouterView,
          redirect: { name: 'home' },
          children: [
            {
              path: 'status',
              name: 'status',
              component: Status
            },
            {
              path: 'category',
              name: 'category',
              component: Category
            },
            {
              path: 'rank_report',
              name: 'rank_report',
              component: RankReports
            },
            {
              path: 'indexation-tracking',
              name: 'indexation-tracking',
              component: BaseRouterView,
              children: [
                {
                  path: '',
                  name: 'indexation-tracking',
                  component: SerpTracker
                },
                {
                  path: ':id',
                  name: 'indexation-tracking',
                  component: SerpTrackerViewHistory,
                  props: true
                }
              ]
            },
            ...systemRoutes,
            {
              path: 'authorization',
              name: 'authorization',
              redirect: { name: 'user-groups' },
              component: Authorization,
              children: [
                {
                  path: 'user-groups',
                  name: 'user-groups',
                  component: Groups,
                },
                {
                  path: 'security-permissions',
                  name: 'security-permissions',
                  component: Permissions,
                }
              ]
            }
          ]
        }
      ]
    },
    {
      path: '/access-denied',
      name: 'access-denied',
      component: AccessDenied
    },
    {
      path: '/user',
      name: 'auth',
      component: Auth,
      redirect: { name: 'login' },
      children: [
        {
          path: 'sign-in',
          name: 'login',
          component: Login,
          props: (route) => ({ next: route.query.next })
        },
        {
          path: 'forgot-password',
          name: 'forgot-password',
          component: ForgotPassword
        },
        {
          path: 'register',
          name: 'register',
          component: Register
        },
        {
          path: 'reset-password/:id',
          name: 'reset-password',
          component: Register
        }
      ]
    },
    {
      path: '*',
      name: 'not-found',
      component: NotFound
    }
  ]
})

// go to start page for application
router.navigateToEntryPoint = function () {
  this.replace('/reports')
}

const checkAccess = async (to, from, next) => {
  if (!store.state.currentUser._id) {
    await store.dispatch('getCurrentSession')
  }
  if (!store.state.currentUser._id) {
    if (!to.path.startsWith('/user/')) {
      const isLoginPages = to.path.startsWith('/user/')
      const nextUrl = !isLoginPages ? '?next=' + to.path : ''
      next({ path: '/user/sign-in' + nextUrl })
    } else {
      next()
    }
    return
  }
  if (!store.state.allowedRoutes.length) {
    if (!to.path.startsWith('/setup-wizard')) {
      next({ path: '/setup-wizard' })
    } else {
      next()
    }
    return
  }
  // const accountInGoodStanding = !!store.state.agency.billingData.stripe_account_standing
  // if (to.path !== '/system/billing' && !accountInGoodStanding) { 
  //   next({ path: '/system/billing' })
  //   return
  // }
  const hasAccess = store.state.applicationRoutes.some(route => {
    return route.allowed && route.regex.test(to.path) && route.name !== 'not-found'
  })
  if (hasAccess) {
    next()
  } else {
    let isLastValid = false
    if (from.path) {
      isLastValid = store.state.applicationRoutes.some(route => (
        route.regex.test(from.path) && route.allowed && route.name !== 'not-found'
      ))
    }
    let homePage = store.state.applicationRoutes.find(route => route.name === 'strategy' && route.allowed)?.path
    homePage = homePage || store.state.applicationRoutes.find(route => route.name === 'Progress' && route.allowed)?.path
    homePage = homePage || '/strategy/url-planner'
    const nextPage = isLastValid ? from.path : homePage
    next({ path: nextPage })
  }
  return
}

// navigation guard(check access)
router.beforeEach((to, from, next) => {
  store.state.previousPage = from.path
  checkAccess(to, from, next)
  store.state.agency.shouldSelectPaymentPlan = false
  return
})

router.afterEach(() => {
  window.scrollTo(0, 0)
})

export default router
